/**
 * **Add a section** — the picker (T-4.33, T-4.35).
 *
 * The owner's ask was *"page building like core cms and website"*, and the part
 * of that a select box cannot do is the part that matters: a firm choosing
 * between *Showcase*, *Split feature* and *Feature grid* is choosing a shape,
 * and three names in a dropdown do not tell it apart. So every choice carries a
 * drawing of what it looks like (`SectionShape`), and the name is underneath
 * rather than instead.
 *
 * Four things are said on the card rather than discovered afterwards, because
 * every one of them is invisible once the section is on the page:
 *
 * * **this firm's template draws it in its own markup** (ADR-0020 §2);
 * * **it reads live data**, so the published page changes without a republish
 *   (FR-I14 rule 3);
 * * **the firm's conduct policy is keeping it off the site**, with the switch
 *   named (ADR-0014 §3) — shown *before* the firm writes twelve testimonials
 *   into a section that will not render;
 * * **there is nothing in the catalogue for it to draw** (w8h, finding 3) — the
 *   fourth, and the one a real firm met: a services section over an empty
 *   catalogue renders nothing, correctly, and says nothing about why.
 *
 * ## Keyboard
 *
 * Everything here is a `<button>` inside `Dialog`, which traps focus and closes
 * on Escape, so the whole picker is reachable with Tab alone and every choice
 * fires on Enter and Space. The search box is the shortcut, not the only route.
 * Nothing is a `div` with an `onClick`, and the drawing is `aria-hidden` — a
 * screen reader hears the section's name and the service's own description of
 * it, which is the sentence a wireframe is a picture of.
 */

import { Badge, Dialog, Input, TONE_TEXT } from '@fiveox/ui'
import { Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { BlockSchema, Vocabulary } from '../../lib/api/site'
import { SectionShape } from './SectionShape'
import {
  emptyCatalogueNote,
  liveDataNote,
  pickerGroups,
  sectionLabel,
  suppression,
  themeNote,
} from './sections'

export interface PickerContext {
  templateCode: string | null
  templateName: string | null
  vocabulary: Vocabulary | null
  /** The firm's conduct switches, resolved — `resolvedSwitches`. */
  switches: Record<string, boolean>
  /**
   * How many of the firm's services are set to show on its website, or `null`
   * when this screen could not find out — w8h, finding 3.
   *
   * Zero is why a services section draws nothing, and it is the one reason a
   * firm cannot arrive at by looking at its own website. It belongs on the
   * picker's card rather than only on the section afterwards: the card is where
   * a firm is still deciding, and *"this will draw nothing yet"* is worth
   * knowing before twelve fields are filled in.
   */
  visibleServices: number | null
}

export function SectionPicker({
  open,
  onClose,
  schemas,
  addable,
  context,
  where,
  onAdd,
}: {
  open: boolean
  onClose: () => void
  /** Every section the service publishes, in its order. */
  schemas: BlockSchema[]
  /** Those that may be added to *this* page — `addableBlocks`. The rest are
   *  shown and disabled rather than hidden: a firm hunting for "Hero" and not
   *  finding it concludes the product has lost it. */
  addable: Set<string>
  context: PickerContext
  /** Where the new section will go, in words — *It goes after “About”.*
   *  (Wave 14C: *Add a section* is offered at the top and between sections.) */
  where?: string
  onAdd: (schema: BlockSchema) => void
}) {
  const [q, setQ] = useState('')

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return schemas
    return schemas.filter((schema) =>
      `${sectionLabel(schema, schema.type)} ${schema.description}`.toLowerCase().includes(needle),
    )
  }, [q, schemas])

  return (
    <Dialog open={open} onClose={onClose} title="Add a section" width="xl">
      <div className="space-y-4">
        {where && (
          <p className="text-sm text-muted-foreground" data-picker-where="">
            {where}
          </p>
        )}
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          prefix={<Search size={16} aria-hidden="true" />}
          aria-label="Search the sections"
          placeholder="Search — quotes, figures, steps…"
        />

        {matches.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No section matches “{q.trim()}”.
          </p>
        ) : (
          // C21 (T-4.90): thirty-eight in one grid is a scroll, so they sit
          // in `SECTION_GROUPS`' groups — a search keeps the groups and drops
          // the ones it emptied.
          pickerGroups(matches).map((group) => (
            <section key={group.key} data-picker-group={group.key} aria-labelledby={`picker-group-${group.key}`}>
              <h3
                id={`picker-group-${group.key}`}
                className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {group.label}
              </h3>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.schemas.map((schema) => (
                  <li key={schema.type}>
                    <Choice
                      schema={schema}
                      context={context}
                      disabled={!addable.has(schema.type)}
                      onAdd={() => {
                        onAdd(schema)
                        onClose()
                      }}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </Dialog>
  )
}

function Choice({
  schema,
  context,
  disabled,
  onAdd,
}: {
  schema: BlockSchema
  context: PickerContext
  disabled: boolean
  onAdd: () => void
}) {
  const label = sectionLabel(schema, schema.type)
  const theme = themeNote(context.templateCode, context.templateName, schema.type)
  const live = liveDataNote(schema.type)
  const hidden = suppression(schema.type, context.vocabulary, context.switches)
  const empty = emptyCatalogueNote(schema.type, context.visibleServices)

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onAdd}
      // The accessible name is the whole sentence a person needs to decide:
      // what it is called, and why it is greyed out if it is.
      aria-label={disabled ? `${label} — already on this page` : `Add ${label}`}
      className="group flex h-full w-full flex-col gap-2 rounded-xl border border-border bg-surface-raised p-3 text-left transition enabled:hover:border-primary enabled:hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-55"
    >
      <SectionShape schema={schema} />

      <span className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {!disabled && (
          <Plus
            size={13}
            aria-hidden="true"
            className="text-muted-foreground opacity-0 transition group-hover:opacity-100"
          />
        )}
      </span>
      <span className="text-xs leading-snug text-muted-foreground">{schema.description}</span>

      {/* w8h, finding 3. **The sentence, not just a badge.** "Nothing to show
          yet" tells a firm the same nothing its website does; the remedy is a
          switch three screens away and has to be named here. */}
      {empty && <span className={`text-xs leading-snug ${TONE_TEXT.warning}`}>{empty}</span>}

      <span className="mt-auto flex flex-wrap gap-1 pt-1">
        {disabled && <Badge tone="neutral">Already on this page</Badge>}
        {theme && <Badge tone="info">{context.templateName} draws this</Badge>}
        {live && <Badge tone="info">Reads live data</Badge>}
        {hidden && <Badge tone="warning">Your policy hides this</Badge>}
        {empty && <Badge tone="warning">Nothing to show yet</Badge>}
      </span>
    </button>
  )
}
